/**
*****************************************
*                                       *
* NAGARRO  - CSX - CONFIDENTIAL         *
*                                       *
*****************************************
*/

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { MessageDetails } from '../model/message-details.model';
import { Tab } from '../model/tab.model';
import { DataStoreService } from '../services/datastore.service';
import { environment } from './../../../environments/environment';
import { LmsEventDetails } from './../model/lms-event-details.model';
import { ConfigService } from './config.service';
import { MessageHandlerService } from './message-handler.service';
import { UtilService } from './util.service';


/**
 * @export
 * @class TabService
 */
@Injectable()
export class TabService {
    private tabStore = new Array<Tab>();
    tabStoreObservable: Observable<Array<Tab>>;
    private tabStoreBS: BehaviorSubject<Array<Tab>>;
    private configData: any;

    /**
     * Creates an instance of TabService.
     * @param {UtilService} utilService
     * @param {ConfigService} configService
     * @param {MessageHandlerService} messageHandlerService
     * @param {Router} router
     * @param {DataStoreService} dataStoreService
     *
     * @memberOf TabService
     */
    constructor(private utilService: UtilService,
        private configService: ConfigService,
        private messageHandlerService: MessageHandlerService,
        private router: Router,
        private dataStoreService: DataStoreService) {
        this.tabStoreBS = <BehaviorSubject<Array<Tab>>>new BehaviorSubject({});
        this.tabStoreObservable = this.tabStoreBS.asObservable();
        this.tabStoreBS.next(this.getTabs());
        this.configData = this.configService.getConfigData();
    }

    /**
     * This is used to get Tab from USer service
     * @private
     * @returns
     * @memberOf TabService
     */
    getTabs() {
        return this.utilService.deepClone(this.tabStore);
    }

    /**
     * by this method we are getting tab which
     *  match witch route param id
     * @param {number} id
     * @memberOf TabService
     */

    getTabById(id: number) {
        const tab = this.tabStore.filter(data => {
            return data.id.toString() === id.toString();
        });
        if (tab) {
            return this.utilService.deepClone(tab);
        }
    }

    getTabDataBySubType(type: string, subType: string) {
        const tab = this.tabStore.filter(data => {
            return (data.type.toLowerCase() === type.toLowerCase() && data.subType.toLowerCase() === subType.toLowerCase());
        });
        if (tab) {
            return this.utilService.deepClone(tab);
        }
    }

    /**
     * This is used to notify any tab changes related
     * @param {LmsEventDetails} lmsEventDetails
     * @memberOf TabService
     */
    notifyChange(lmsEventDetails: LmsEventDetails): any {
        if (lmsEventDetails.type === this.configData.tab.event.add) {
            return this.addTab(lmsEventDetails.data);
        } else if (lmsEventDetails.type === this.configData.tab.event.update) {
            return this.updateTab(lmsEventDetails.data);
        } else if (lmsEventDetails.type === this.configData.tab.event.remove) {
            return this.removeTab(lmsEventDetails.data);
        } else if (lmsEventDetails.type === this.configData.tab.event.select) {
            return this.selectTab(lmsEventDetails.data);
        } else if (lmsEventDetails.type === this.configData.tab.event.refresh) {
            return this.refreshTab(lmsEventDetails.data);
        }
    }

    /**
     * This is used to add Tab
     * @private
     * @param {Tab} tab
     * @memberOf TabService
     */
    private addTab(tab: Tab) {
        if (this.tabStore.filter(data => { return data.type === tab.type; }).length >= 20) {
            this.messageHandlerService.handleMessage(<MessageDetails>({
                type: this.configData.message.type.client,
                level: this.configData.message.level.error,
                target: this.configData.message.target.page,
                message: this.configData.messages.maxTab
            }));
            return null;
        } else {
            if (!tab.id) {
                return null;
            }
            if (!tab.description) {
                tab.description = this.configData.tab.defaultDescription;
            }
            tab.tabSearchParameter = tab.tabSearchParameter ? tab.tabSearchParameter : {};
            tab.mode = tab.mode ? tab.mode : 'view';

            if (!tab.subTypeDetail[tab.subType.toLowerCase()].status) {
                tab.subTypeDetail[tab.subType.toLowerCase()].status = this.configData.tabStatus.blank;
            }
            this.tabStore.push(this.utilService.deepClone(tab));
            this.tabStore = this.tabStore.map((data) => {
                data.selected = (tab.id === data.id);
                return data;
            });
            this.publishChange();
            return this.getTabById(tab.id);
        }
    }

    /**
     * @param {Tab} tab
     * @param {Boolean} [editMode]
     * @returns
     *
     * @memberOf TabService
     */
    public copyTab(tab: Tab, editMode?: Boolean) {
        tab.selected = false;
        this.tabStore.push(this.utilService.deepClone(tab));
        if (!editMode) {
            this.tabStore = this.tabStore.map((data) => {
                data.selected = (tab.id === data.id);
                return data;
            });
        }
        this.router.navigateByUrl(`/${tab.type.toLowerCase()}/${tab.subType.toLowerCase()}/${tab.id}`,
            { skipLocationChange: environment.hideUrl });
        this.publishChange();
        return true;
    }

    /**
     * This is used to refresh Tab
     * @private
     * @param {Tab} tab
     * @memberOf TabService
     */
    private refreshTab(tab: Tab) {
        this.tabStore = this.tabStore.map((data) => {
            if (data.id === tab.id && data.subType === tab.subType) {
                const lTab: Tab = this.utilService.deepClone(tab);
                lTab.subTypeDetail[lTab.subType.toLowerCase()].status = this.configData.tabStatus.refresh;
                lTab.mode = 'view';
                lTab.lastRefreshTime = data.lastRefreshTime;
                return lTab;
            } else {
                return data;
            }
        });
        this.publishChange();
    }

    /**
     * This is used to remove Tab
     * @private
     * @param {Tab} tab
     * @memberOf TabService
     */
    private removeTab(tab: Tab) {
        const selectedTab = this.tabStore.filter(data => data.selected == true);
        if (selectedTab.length > 0 && selectedTab[0].type == tab.type) {
            const previousTabs = this.tabStore.filter((data) => { return data.type == tab.type && data.id < tab.id;});
            const nextTabs = this.tabStore.filter((data) => { return data.type == tab.type && data.id > tab.id;});
            const pageCount = tab.type !== 'Yard-Local' ? 6 : 4;
            if (previousTabs.length > pageCount) {
                for (let i = 0; i < this.tabStore.length; i++) {
                    this.tabStore[i].selected = false;
                }
                const index = _.findIndex(this.tabStore, function (o) { return o && o.id === previousTabs[0].id; });
                this.tabStore[index].selected = true;
            } else if (nextTabs.length > 0) {
                for (let i = 0; i < this.tabStore.length; i++) {
                    this.tabStore[i].selected = false;
                }
                const index = _.findIndex(this.tabStore, function (o) { return o && o.id === nextTabs[0].id; });
                this.tabStore[index].selected = true;
            }
        }
        this.tabStore = this.tabStore.filter((data) => { return tab.id !== data.id; });
        this.publishChange();
    }

    /**
     * This is used to select the Tab
     * @private
     * @param {Tab} tab
     * @memberOf TabService
     */
    private selectTab(tab: Tab) {
        if (!tab) {
            tab = { tabSearchParameter: {} };
        }

        for (let i = 0; i < this.tabStore.length; i++) {
            this.tabStore[i].selected = false;
        }
        if (_.findIndex(this.tabStore, function (o) { return o && o.id === tab.id; }) > -1) {
            this.tabStore[_.findIndex(this.tabStore, function (o) { return o && o.id === tab.id; })].selected = true;
        }
        if (this.isChangeToBeNotified(tab)) {
            this.publishChange();
        }
    }

    /**
     * This is used to update Tab
     * @private
     * @param {Tab} tab
     * @memberOf TabService
     */
    private updateTab(tab: Tab) {
        if (!tab) {
            tab = { tabSearchParameter: {} };
        }
        for (let i = 0; i < this.tabStore.length; i++) {
            this.tabStore[i].selected = false;
        }
        if (_.findIndex(this.tabStore, function (o) { return o && o.id === tab.id; }) > -1) {
            this.tabStore[_.findIndex(this.tabStore, function (o) { return o && o.id === tab.id; })].selected = true;
        }
        if (this.isChangeToBeNotified(tab)) {
            this.tabStore = this.tabStore.map((data) => {
                if (data.id === tab.id) {
                    return this.utilService.deepClone(tab);
                } else {
                    return data;
                }
            });
            if (_.findIndex(this.tabStore, function (o) { return o && o.id === tab.id; }) > -1) {
                this.tabStore[_.findIndex(this.tabStore, function (o) { return o && o.id === tab.id; })].selected = true;
            }
            this.publishChange();
        }
    }

    /**
     * @param {Array<Tab>} tabPreferences
     * @param {Tab} firstTabPreference
     *
     * @memberOf TabService
     */
    bulkTabCreation(tabPreferences: Array<Tab>, firstTabPreference: Tab) {

        if (firstTabPreference) {
            if (!firstTabPreference.subTypeDetail[firstTabPreference.subType.toLowerCase()].status) {
                firstTabPreference.subTypeDetail[firstTabPreference.subType.toLowerCase()].status = this.configData.tabStatus.blank;
            }

            if (firstTabPreference.tabSearchParameter && firstTabPreference.tabSearchParameter.searchText) {
                firstTabPreference.subTypeDetail[firstTabPreference.subType.toLowerCase()].status = this.configData.tabStatus.refresh;
            }
            firstTabPreference.id = new Date().getTime();
            if (!firstTabPreference.description) {
                firstTabPreference.description = this.configData.tab.defaultDescription;
            }
            firstTabPreference.tabSearchParameter = firstTabPreference.tabSearchParameter ? firstTabPreference.tabSearchParameter : {};
            firstTabPreference.mode = firstTabPreference.mode ? firstTabPreference.mode : 'view';

            if (firstTabPreference.tabSearchParameter.searchText) {
                if (firstTabPreference.tabSearchParameter.noThroughTrains) {
                    this.router.navigateByUrl(
                        `/${firstTabPreference.type.toLowerCase()}/${firstTabPreference.subType.toLowerCase()}/${new Date().getTime()}?search=${firstTabPreference.tabSearchParameter.searchText}
                    &noThroughTrains=${firstTabPreference.tabSearchParameter.noThroughTrains}`,
                        { skipLocationChange: environment.hideUrl });
                } else {
                    this.router.navigateByUrl(
                        `/${firstTabPreference.type.toLowerCase()}/${firstTabPreference.subType.toLowerCase()}/${new Date().getTime()}?search=${firstTabPreference.tabSearchParameter.searchText}`,
                        { skipLocationChange: environment.hideUrl });
                }
            } else {
                this.router.navigateByUrl(
                    `/${firstTabPreference.type.toLowerCase()}/${firstTabPreference.subType.toLowerCase()}/${new Date().getTime()}`,
                    { skipLocationChange: environment.hideUrl });
            }
            this.publishChange();
        }
        setTimeout(() => {
            for (let i = 0; i < tabPreferences.length; i++) {
                const tab = tabPreferences[i];
                tab.id = this.utilService.generateUniqueId(i);

                tab.description = `${tab.tabSearchParameter.searchText ? tab.tabSearchParameter.searchText + ' - ' : 'New Tab'}${tab.tabSearchParameter.searchText ? tab.subType : ''}`;
                tab.tabSearchParameter = tab.tabSearchParameter ? tab.tabSearchParameter : {};
                tab.mode = tab.mode ? tab.mode : 'view';
                if (tab.tabSearchParameter && tab.tabSearchParameter.searchText) {
                    tab.subTypeDetail[tab.subType.toLowerCase()].status = this.configData.tabStatus.refresh;
                }
                if (!tab.subTypeDetail[tab.subType.toLowerCase()].status) {
                    tab.subTypeDetail[tab.subType.toLowerCase()].status = this.configData.tabStatus.blank;
                }
                this.tabStore.push(this.utilService.deepClone(tab));
            }
            this.publishChange();
        }, 100);

    }
    /**
     * This is used to publish changes to behavior subject
     * @private
     * @memberOf TabService
     */
    private publishChange() {
        this.tabStoreBS.next(this.getTabs());
    }

    /**
     * Check whether tab exists or not
     * @param {Tab} tab
     * @returns
     * @memberOf TabService
     */
    isChangeToBeNotified(tab: Tab) {
        const existingTab = this.tabStore.find((data) => { return data.id === tab.id && data.subType === tab.subType; });
        return (!existingTab || JSON.stringify(this.sortObject(existingTab)) !== JSON.stringify(this.sortObject(tab)));
    }

    /**
     * @memberOf TabService
     */
    removeTabs() {
        this.tabStore = [];
        this.tabStoreBS.next(this.tabStore);
    }

    /**
     * @param {string} type
     * @returns
     *
     * @memberOf TabService
     */
    isAddTabAllowed(type: string) {
        return !(this.tabStore.filter(data => { return data.type === type; }).length >= 20);
    }

    /**
     * @param {any} o
     * @returns
     *
     * @memberOf TabService
     */
    sortObject(o) {
        const sorted = {};
        let key;
        const a = [];

        for (key in o) {
            if (o.hasOwnProperty(key)) {
                a.push(key);
            }
        }

        a.sort();

        for (key = 0; key < a.length; key++) {
            sorted[a[key]] = o[a[key]];
        }
        return sorted;
    }

    /**
     * Find tab in tab store by type, sub type and search text.
     * @param {string} type
     * @param {string} subType
     * @param {string} searchText
     * @returns
     * @memberof TabService
     */
    findTabByTypeSubTypeSearchText(type: string, subType: string, searchText: string) {
        let tab: Tab = {};
        for (let i = 0; i < this.tabStore.length; i++) {
            const data = this.tabStore[i];
            if (data && data.type && data.subType && data.tabSearchParameter && data.tabSearchParameter.searchText
                && data.type.toLowerCase() === type.toLowerCase()
                && data.subType.toLowerCase() === subType.toLowerCase()
                && data.tabSearchParameter.searchText.toLowerCase() === searchText.toLowerCase()) {
                tab = data;
            }
        }
        return tab;
    }

    /**
     * Find tab in tab store by type, sub type and search text.
     * @param {string} type
     * @param {string} searchText
     * @returns
     * @memberof TabService
     */
    findTabByTypeSearchText(type: string, searchText: string) {
        let tab: Tab = {};
        for (let i = 0; i < this.tabStore.length; i++) {
            const data = this.tabStore[i];
            if (data && data.type && data.tabSearchParameter && data.tabSearchParameter.searchText
                && data.type.toLowerCase() === type.toLowerCase()
                && data.tabSearchParameter.searchText.toLowerCase() === searchText.toLowerCase()) {
                tab = data;
            }
        }
        return tab;
    }
    /**
     * Check for valid tab redirection.
     * @param {string} type
     * @param {string} subType
     * @param {string} searchText
     * @returns {boolean}
     * @memberof TabService
     */
    isTabRedirectionAllowed(type: string, subType: string): boolean {
        const isTabAllowed = ((this.tabStore.filter(data => {
            return (data.type.trim().toLowerCase() === type.trim().toLowerCase());
        })).length < 20);
        return isTabAllowed;
    }
}
